import React from 'react';

import { FormattedMessage } from 'react-intl';

import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';

import { Recipient, SenderPrepay, PrepaidDuty } from './constants';
import { dutiesAndTaxesMessages } from './messages';
import { FormattedCurrencyContainer } from '../../components/formatted-currency/container';
import { useFulfillmentPlannerContext } from '../context';

const DD_OPTIONS_WITH_PRICE = [
  { descriptor: dutiesAndTaxesMessages.recipientPaysOption, value: Recipient },
  {
    descriptor: dutiesAndTaxesMessages.prepayDutiesOption,
    value: SenderPrepay,
  },
];
const DD_OPTIONS_WITHOUT_PRICE = [
  { descriptor: dutiesAndTaxesMessages.recipientPaysOption, value: Recipient },
  {
    descriptor: dutiesAndTaxesMessages.prepayDutiesOptionWithoutPrice,
    value: SenderPrepay,
  },
];

/** @typedef {import('react-intl').MessageDescriptor} MessageDescriptor */
/**
 * Dropdown component for selecting duties and taxes payment options
 * @param {object} props - Component props
 * @param {string} [props.id] - Optional ID for the dropdown
 * @param {string} [props.dropdownMenuType] - Type of dropdown menu
 * @param {string|MessageDescriptor} [props.inlineMessage] - Inline message to display (string or message descriptor)
 * @param {string} [props.inlineMessageType] - Type of inline message
 * @param {() => void} props.onChange - Required callback function when selection changes
 * @param {string} [props.selectedOptionValue] - Currently selected option value (Recipient, SenderPrepay, PrepaidDuty, or 'Prepaid')
 * @param {boolean} [props.shouldHidePrice] - Whether to hide price information
 * @returns {JSX.Element} The duties and taxes dropdown component
 */
export const DutiesAndTaxesDropdown = ({
  id,
  dropdownMenuType,
  inlineMessage = '',
  inlineMessageType = '',
  onChange,
  selectedOptionValue = Recipient,
  shouldHidePrice = false,
}) => {
  const { availableDutiesOptions = [] } = useFulfillmentPlannerContext();
  const senderPrepayOption = availableDutiesOptions.find((option) => option?.additionalOptionValue?.prepaidDuty === 'SenderPrepay');
  const { value, code } = senderPrepayOption?.additionalOptionValue?.price || {};

  const ddpPrice = senderPrepayOption ? <FormattedCurrencyContainer currencyCode={code} value={value} /> : null;
  const DD_OPTIONS = shouldHidePrice ? DD_OPTIONS_WITHOUT_PRICE : DD_OPTIONS_WITH_PRICE;

  // Map "Prepaid" and "PrepaidDuty" to "SenderPrepay" for compatibility
  const normalizedSelectedValue =
    selectedOptionValue === 'Prepaid' || selectedOptionValue === PrepaidDuty ? SenderPrepay : selectedOptionValue;

  const selectedOption = DD_OPTIONS.find((opt) => opt.value === normalizedSelectedValue) || DD_OPTIONS[0];
  return (
    <DropdownMenu
      dropdownMenuShouldHaveWidthOfButton
      dropdownMenuType={dropdownMenuType}
      fullWidth
      id={id}
      inlineMessage={inlineMessage}
      inlineMessageType={inlineMessageType}
      variant='form'
    >
      <DropdownMenuToggler theme='white'>
        <FormattedMessage {...selectedOption.descriptor} values={{ ddpPrice }} />
      </DropdownMenuToggler>
      {DD_OPTIONS.map((option, index) => (
        <DropdownMenuItem key={index} onClick={() => onChange(option.value)} selected={option.value === selectedOption.value}>
          <FormattedMessage {...option.descriptor} values={{ ddpPrice }} />
        </DropdownMenuItem>
      ))}
    </DropdownMenu>
  );
};
